import {
  useLazyContextProvider,
  useRequiredContext,
} from "@redotech/react-util/context";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getCustomerSearch } from "@redotech/redo-merchant-app-common/client/shopify";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { MarketingPermissions } from "@redotech/redo-model/permissions";
import {
  CollectionsContext,
  DiscountMethodsContext,
  DiscountMethodsContextI,
  DiscountsContext,
  ProductsContext,
  ShopifySegmentsContext,
} from "@redotech/redo-web/discounts/discount-selector";
import {
  convertShopifyIdToGid,
  ShopifyIdType,
} from "@redotech/shopify-util/id";
import { memo, ReactNode, useMemo } from "react";

export const DiscountsService = memo(function DiscountsService({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const marketingRpc = useRequiredContext(RedoMarketingRpcClientContext);
  const merchantClient = useRequiredContext(RedoMerchantClientContext);

  const canViewMarketing = useHasPermission(
    MarketingPermissions.VIEW_MARKETING,
  );

  const discounts = useLazyContextProvider(async (signal) => {
    if (!canViewMarketing) {
      return [];
    }
    return marketingRpc.getDiscountsByTeam({}, { signal });
  });

  const collections = useLazyContextProvider(async (signal) => {
    if (!canViewMarketing) {
      return [];
    }
    const collections = await marketingRpc.getSegmentCollections(null, {
      signal,
    });
    return collections.map((c) => {
      return {
        id: convertShopifyIdToGid(c.id, ShopifyIdType.Collection),
        name: c.title,
      };
    });
  });

  const products = useLazyContextProvider(async (signal) => {
    if (!canViewMarketing) {
      return [];
    }
    const products = await marketingRpc.getSegmentProducts(null, { signal });
    return products.map((p) => ({
      id: convertShopifyIdToGid(p.id, ShopifyIdType.Product),
      name: p.title,
    }));
  });

  const shopifySegments = useLazyContextProvider(async (signal) => {
    if (!canViewMarketing) {
      return [];
    }
    try {
      return await marketingRpc.getShopifySegments({}, { signal });
    } catch (error) {
      return [];
    }
  });

  const discountMethods = useMemo(() => {
    const methods: DiscountMethodsContextI = {
      createDiscount: async ({ discountConfiguration }) => {
        const discount = await marketingRpc.createDiscount({
          discountConfiguration,
        });
        discounts.invalidate();
        return discount._id;
      },
      deleteDiscount: async (discountId) => {
        await marketingRpc.deleteDiscount({ _id: discountId });
        discounts.invalidate();
      },
      updateDiscount: async ({ discountId, discountConfiguration }) => {
        await marketingRpc.updateDiscount({
          _id: discountId,
          configuration: discountConfiguration,
        });
        discounts.invalidate();
      },
      searchCustomers: async (searchText, signal) => {
        if (!canViewMarketing || !searchText.trim()) {
          return [];
        }
        try {
          const response = await getCustomerSearch(merchantClient, {
            search: searchText,
            signal,
          });
          return response.customers || [];
        } catch (error) {
          if (!signal.aborted) {
            console.error("Customer search failed:", error);
          }
          return [];
        }
      },
    };
    return methods;
  }, [marketingRpc, merchantClient, canViewMarketing, discounts]);

  return (
    <DiscountMethodsContext.Provider value={discountMethods}>
      <CollectionsContext.internalContext.Provider value={collections}>
        <ProductsContext.internalContext.Provider value={products}>
          <DiscountsContext.internalContext.Provider value={discounts}>
            <ShopifySegmentsContext.internalContext.Provider
              value={shopifySegments}
            >
              {children}
            </ShopifySegmentsContext.internalContext.Provider>
          </DiscountsContext.internalContext.Provider>
        </ProductsContext.internalContext.Provider>
      </CollectionsContext.internalContext.Provider>
    </DiscountMethodsContext.Provider>
  );
});
